import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNodeByKey } from "lexical";
import { useCallback } from "react";
import type { BrandInfoSelected, TemplateInfo } from "../../../brand-kit/types";
import { useCreateDesignHuddleProject } from "../../../design-huddle/use-design-huddle-project-create";
import { useClaims } from "../../../generic/hooks/use-claims";
import { $createDesignHuddleNode } from "../nodes/design-huddle-mui";
import { $isDesignHuddleNode } from "../nodes/design-huddle-node";
import { $removePlaceholderImage } from "../nodes/image-node-mui";
import { $insertNode } from "../utils/insert-node";

export const useInsertDesignHuddleEditor = (): ((
  templateId: string,
  branding: BrandInfoSelected,
  template?: TemplateInfo,
) => Promise<void>) => {
  const [editor] = useLexicalComposerContext();
  const user = useClaims();

  const createProject = useCreateDesignHuddleProject();

  return useCallback(
    async (templateId: string, branding: BrandInfoSelected, template?: TemplateInfo) => {
      if (!user) return;

      let key: string | undefined;
      editor.update(
        () => {
          $removePlaceholderImage();
          const placeholder = $createDesignHuddleNode(
            editor._headless,
            user["http://changeengine.com/account_id"],
          );
          $insertNode(placeholder);
          key = placeholder.getKey();
        },
        { discrete: true },
      );

      const data = await createProject(templateId, branding, template);
      if (!data) throw new Error("Failed to create project");

      const { project_id } = data;

      editor.update(() => {
        if (key) {
          const node = $getNodeByKey(key);
          if ($isDesignHuddleNode(node)) {
            node.setProjectId(project_id);
          }
        }
      });
    },
    [createProject, editor, user],
  );
};
